import { useMemo, useState, type ReactNode } from "react";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { Ago, EmptyBlock } from "@/monitoring/components/primitives";
import { normalizeName, parseStamp, recipeUsage } from "../model";
import type { CatalogIngredient, CatalogResponse } from "../types";
import { IngredientEditor } from "./ingredient-editor";
import { ProductEditor } from "./product-editor";
import { RecipeEditor } from "./recipe-editor";
import { IngredientChip } from "./shared";

/** which record an editor is open for: an id, "new", or none */
type Editing = string | typeof NEW | null;
const NEW = Symbol("new");

const matches = (query: string, ...texts: (string | null | undefined)[]) => {
  const q = normalizeName(query);
  return !q || texts.some((text) => text && normalizeName(text).includes(q));
};

function Toolbar({
  query,
  onQuery,
  placeholder,
  addLabel,
  onAdd,
  children,
}: {
  query: string;
  onQuery: (query: string) => void;
  placeholder: string;
  addLabel: string;
  onAdd: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-0 flex-1 sm:max-w-sm">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input value={query} onChange={(event) => onQuery(event.target.value)} placeholder={placeholder} className="h-9 pl-8" />
      </div>
      {children}
      <Button className="h-9 shrink-0" onClick={onAdd}>
        <Plus className="mr-1 h-4 w-4" />
        {addLabel}
      </Button>
    </div>
  );
}

/** "changed by X · 5 min ago" of a record */
function Changed({ by, at }: { by: string | null | undefined; at: string | null | undefined }) {
  const { t } = useLocalization();
  if (!at) return null;
  return (
    <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
      <span className="min-w-0 truncate">{t("catalog.list.changedBy", { who: by ?? "?" })}</span>
      <span aria-hidden>·</span>
      <Ago ts={parseStamp(at)} className="shrink-0" />
    </span>
  );
}

const rowClass =
  "flex w-full min-w-0 flex-col gap-2 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function RecipesTab({ catalog, onReload }: { catalog: CatalogResponse; onReload: () => Promise<unknown> }) {
  const { t, tp } = useLocalization();
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<Editing>(null);
  const byId = useMemo(() => new Map(catalog.ingredients.map((i) => [i.id, i])), [catalog.ingredients]);
  const recipeIds = useMemo(() => new Set(catalog.recipes.map((r) => r.id)), [catalog.recipes]);
  const shown = catalog.recipes.filter((recipe) =>
    matches(query, recipe.id, recipe.name, ...recipe.components.map((c) => byId.get(c.ingredient_id)?.name)),
  );
  const current = typeof editing === "string" ? catalog.recipes.find((r) => r.id === editing) ?? null : null;

  return (
    <div className="flex flex-col gap-3">
      <Toolbar
        query={query}
        onQuery={setQuery}
        placeholder={t("catalog.recipes.search")}
        addLabel={t("catalog.recipes.add")}
        onAdd={() => setEditing(NEW)}
      />
      <Legend />
      {shown.length === 0 ? (
        <EmptyBlock>{t("catalog.list.empty")}</EmptyBlock>
      ) : (
        <ul className="divide-y rounded-md border">
          {shown.map((recipe) => {
            const checked = recipe.components.filter((c) => byId.get(c.ingredient_id)?.detector_classes.length).length;
            return (
              <li key={recipe.id}>
                <button type="button" className={rowClass} onClick={() => setEditing(recipe.id)}>
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="inline-flex h-6 min-w-[2rem] shrink-0 items-center justify-center rounded-md border px-1.5 font-mono text-xs text-muted-foreground">
                      {recipe.id}
                    </span>
                    <span className="min-w-0 truncate font-medium">{recipe.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {t("catalog.recipe.version", { version: recipe.version })}
                      {" · "}
                      {tp("catalog.recipes.checked", checked, { total: recipe.components.length })}
                    </span>
                    {recipe.version > 1 && (
                      <span className="ml-auto hidden sm:block">
                        <Changed by={recipe.created_by} at={recipe.created_at} />
                      </span>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-wrap gap-1">
                    {recipe.components.map((c) => (
                      <IngredientChip
                        key={c.ingredient_id}
                        ingredient={byId.get(c.ingredient_id)}
                        fallbackId={c.ingredient_id}
                        afterBaking={c.after_baking}
                      />
                    ))}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <RecipeEditor
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        recipe={current}
        ingredients={catalog.ingredients}
        recipeIds={recipeIds}
        onReload={onReload}
      />
    </div>
  );
}

function Legend() {
  const { t } = useLocalization();
  const sample = (classes: string[]): CatalogIngredient => ({ id: "x", name: "", aliases: [], detector_classes: classes, position: 0 });
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <IngredientChip ingredient={{ ...sample(["ham"]), name: t("catalog.legend.checkedSample") }} />
        {t("catalog.legend.checked")}
      </span>
      <span className="flex items-center gap-1.5">
        <IngredientChip ingredient={{ ...sample([]), name: t("catalog.legend.notCheckedSample") }} />
        {t("catalog.legend.notChecked")}
      </span>
    </div>
  );
}

export function IngredientsTab({ catalog, onReload }: { catalog: CatalogResponse; onReload: () => Promise<unknown> }) {
  const { t, tp } = useLocalization();
  const [query, setQuery] = useState("");
  const [onlyChecked, setOnlyChecked] = useState<"all" | "checked" | "unchecked">("all");
  const [editing, setEditing] = useState<Editing>(null);
  const usage = useMemo(() => recipeUsage(catalog.recipes), [catalog.recipes]);
  const shown = [...catalog.ingredients]
    .filter((i) => matches(query, i.id, i.name, ...i.aliases, ...i.detector_classes))
    .filter((i) => onlyChecked === "all" || (onlyChecked === "checked") === i.detector_classes.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
  const current = typeof editing === "string" ? catalog.ingredients.find((i) => i.id === editing) ?? null : null;

  return (
    <div className="flex flex-col gap-3">
      <Toolbar
        query={query}
        onQuery={setQuery}
        placeholder={t("catalog.ingredients.search")}
        addLabel={t("catalog.ingredients.add")}
        onAdd={() => setEditing(NEW)}
      >
        <div className="flex shrink-0 rounded-md border p-0.5" role="group" aria-label={t("catalog.ingredients.filter")}>
          {(["all", "checked", "unchecked"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setOnlyChecked(value)}
              aria-pressed={onlyChecked === value}
              className={cn(
                "h-7 rounded px-2 text-xs text-muted-foreground",
                onlyChecked === value && "bg-muted text-foreground",
              )}
            >
              {t(`catalog.ingredients.filter.${value}`)}
            </button>
          ))}
        </div>
      </Toolbar>
      {shown.length === 0 ? (
        <EmptyBlock>{t("catalog.list.empty")}</EmptyBlock>
      ) : (
        <ul className="divide-y rounded-md border">
          {shown.map((ingredient) => {
            const otherNames = ingredient.aliases.filter((a) => normalizeName(a) !== normalizeName(ingredient.name));
            const used = usage.get(ingredient.id) ?? 0;
            return (
              <li key={ingredient.id}>
                <button type="button" className={rowClass} onClick={() => setEditing(ingredient.id)}>
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <IngredientChip ingredient={ingredient} />
                    <span className="min-w-0 truncate font-mono text-xs text-muted-foreground">{ingredient.id}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{tp("catalog.ingredient.usage", used)}</span>
                    <span className="ml-auto hidden sm:block">
                      <Changed by={ingredient.updated_by} at={ingredient.updated_at} />
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    {ingredient.detector_classes.length > 0 ? (
                      <span className="flex min-w-0 flex-wrap gap-1">
                        {ingredient.detector_classes.map((c) => (
                          <span key={c} className="rounded border border-sky-500/30 bg-sky-500/10 px-1.5 py-0.5 font-mono text-[11px] text-sky-200">
                            {c}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{t("catalog.ingredient.notChecked")}</span>
                    )}
                    {otherNames.length > 0 && (
                      <span className="min-w-0 text-muted-foreground">
                        {t("catalog.ingredient.alsoCalled", { names: otherNames.join(", ") })}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <IngredientEditor
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        ingredient={current}
        ingredients={catalog.ingredients}
        usage={current ? usage.get(current.id) ?? 0 : 0}
        onReload={onReload}
      />
    </div>
  );
}

export function ProductsTab({ catalog, onReload }: { catalog: CatalogResponse; onReload: () => Promise<unknown> }) {
  const { t } = useLocalization();
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<Editing>(null);
  const recipes = useMemo(() => new Map(catalog.recipes.map((r) => [r.id, r])), [catalog.recipes]);
  const shown = catalog.products.filter((p) => matches(query, p.id, p.name, p.recipe_id ? recipes.get(p.recipe_id)?.name : null));
  const current = typeof editing === "string" ? catalog.products.find((p) => p.id === editing) ?? null : null;

  return (
    <div className="flex flex-col gap-3">
      <p className="max-w-3xl text-xs text-muted-foreground">{t("catalog.products.note")}</p>
      <Toolbar
        query={query}
        onQuery={setQuery}
        placeholder={t("catalog.products.search")}
        addLabel={t("catalog.products.add")}
        onAdd={() => setEditing(NEW)}
      />
      {shown.length === 0 ? (
        <EmptyBlock>{t("catalog.list.empty")}</EmptyBlock>
      ) : (
        <ul className="divide-y rounded-md border">
          {shown.map((product) => {
            const recipe = product.recipe_id ? recipes.get(product.recipe_id) : undefined;
            return (
              <li key={product.id}>
                <button type="button" className={cn(rowClass, "flex-row flex-wrap items-center gap-x-3")} onClick={() => setEditing(product.id)}>
                  <span className="inline-flex h-6 min-w-[2rem] shrink-0 items-center justify-center rounded-md border px-1.5 font-mono text-xs text-muted-foreground">
                    {product.id}
                  </span>
                  <span className="min-w-0 truncate font-medium">{product.name}</span>
                  <span className="shrink-0 rounded border px-1.5 py-0.5 text-[11px] text-muted-foreground">
                    {t(`catalog.product.kind.${product.kind}`)}
                  </span>
                  {product.kind === "pizza" && (
                    <span className="min-w-0 truncate text-xs text-muted-foreground">
                      {t("catalog.products.checkedAs", { recipe: recipe ? `${recipe.name} (${recipe.id})` : product.recipe_id ?? "?" })}
                    </span>
                  )}
                  <span className="ml-auto hidden sm:block">
                    <Changed by={product.updated_by} at={product.updated_at} />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <ProductEditor
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        product={current}
        products={catalog.products}
        recipes={catalog.recipes}
        onReload={onReload}
      />
    </div>
  );
}
